/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

//constants
import { FIELDS } from "constants/PostFormFields";
import { PLATFORMS, PlatformSpec } from "constants/Platforms";
import { CHARACTER_LIMITS } from "constants/CharacterLimits";
import {
  getPostCaptionCharacterCount,
  getTwitterEffectiveCaptionLength,
  getTwitterPaidAccountWarning,
} from "../postFormValidator";

//helpers
import { sortAccounts } from "utils/accounts";
import { countCharacter } from "utils/strings";

//components
import { Toggle, TooltipWrapper } from "ui";

//actions
import { togglePlatformLink, updatePostFormField } from "actions/postForm";

//selectors
import { getLoading } from "selectors/commonSelectors";
import { getPlatformFields } from "selectors/postFormSelectors";

import CaptionField from "components/Posts/PostForm/PostCaption/CaptionField";
import { useTrackEvent } from "libs/analytics";
import { PostFormContext as EntitiesPostFormContext } from "@/entities/posts";
import { useFlushPending } from "../platform-fields/flush-registry";

import { AltTextModal } from "../modals/alt-text";
import GmbFields from "../GmbFields/GmbFields";
import YtFields from "../YouTubeFields/YouTubeFields";
import PinterestFields from "../PinterestFields/PinterestFields";
import { PlatformUsage, PostFirstComment, PostTags, PostUrl } from "../index";
import { PlatformFieldsOverlay } from "./PlatformFieldsOverlay";
import { UNLINK_TOGGLE_TW } from "constants/Events";
import { TiktokSwitchNotification } from "../TiktokFields/TiktokSwitchNotification";
import { ThreadsSwitchNotification } from "../threadsFields/ThreadsSwitchNotification";
import { TiktokSwitchAllows } from "../TiktokFields/TiktokSwitchAllows";
import { PostAlerts } from "./PostAlerts";
import { getLinkedInAccountDetail } from "./single-post-form";
import ThreadsReplyControl from "../threadsFields/ThreadsReplyControl";
import { useVideoCover } from "./VideoCoverSelector/hook";
import { VideoCoverSelector } from "./VideoCoverSelector/VideoCoverSelector";
import { VideoReelToggle } from "./VideoReelToggle/VideoReelToggle";
import { mergeGmbPostFields } from "features/post/utils/gmb-post-fields";
import { Post } from "../../../../features/post";
import {
  FormPlatformType,
  FormValidationErrors,
  FormValidationWarnings,
  PlatformFields,
} from "../../../../shared/types/post";
import { AccountType } from "../../../../shared/types/accounts";
import { BulkTabContext } from "../types";
import { SkedEditorHistory } from "../../../../ui/SkedCaption/v2/sked-caption-v2";
import type { Label } from "../../../../entities/posts/components/templates/post-form-base-template";
import { PlatformOptionsCard } from "./platform-options-card";

export interface ClusterPostFormProps {
  post: Post;
  postFormValue: Post;
  errors: FormValidationErrors | undefined;
  warnings: FormValidationWarnings | undefined;
  platforms: PlatformSpec[];
  platformFields: PlatformFields;
  formAccounts: AccountType[];
  updateFields: (fields: Record<string, unknown>) => void;
  updateField: (field: string, value: unknown) => void;
  platformPost: any;
  selectedPlatform: FormPlatformType;
  visibleFields: string[];
  isSummary?: boolean;
  hasPostTags?: boolean;
  hasTikTokPosting: boolean;
  oldTiktokAccounts: AccountType[];
  ttIsNotification: boolean;
  hasThreadsPosting: boolean;
  oldThreadsAccounts: AccountType[];
  thIsNotification: boolean;
  showUrlField: boolean;
  bulkTab: BulkTabContext | undefined;
  onCoverPreviewChange?: (coverUrl: string | null) => void;
  captionEditHistory?: SkedEditorHistory;
  selectedContentLabel?: Label[];
  accountGroupId?: string;
}

export function ClusterPostForm({
  post,
  postFormValue,
  errors,
  warnings,
  platforms,
  platformFields,
  formAccounts,
  updateFields,
  updateField,
  platformPost,
  selectedPlatform,
  visibleFields,
  isSummary,
  hasPostTags,
  hasTikTokPosting,
  oldTiktokAccounts,
  ttIsNotification,
  hasThreadsPosting,
  oldThreadsAccounts,
  thIsNotification,
  showUrlField,
  bulkTab,
  onCoverPreviewChange,
  captionEditHistory,
  selectedContentLabel,
  accountGroupId,
}: ClusterPostFormProps) {
  const dispatch = useDispatch();
  const [openAddAlt, setOpenAddAlt] = useState(false);

  const isLoading = useSelector(getLoading);
  // Warning: If we are using this for creat single post we need to make sure user is initialized properly.
  const trackEvent = useTrackEvent();

  const flushPending = useFlushPending();
  const entitiesFormContext = useContext(EntitiesPostFormContext);
  const store = useStore();
  const currentFormId = bulkTab?.formId ?? post._id;

  // Relink ("Customise") toggle: flush any in-flight edit first so it commits
  // into the override, then — when turning customise OFF on an override that
  // has real user edits — confirm before discarding them.
  const handleToggleUnlink = useCallback(
    (platformKey: FormPlatformType) => {
      // flushPending() dispatches any pending debounced edit synchronously, so
      // read the override from the store AFTER flushing — the `platformFields`
      // prop is from this render's closure and would miss an edit that was just
      // committed (which would otherwise skip the discard confirm).
      flushPending();
      const freshPlatformFields =
        (getPlatformFields(store.getState() as never, currentFormId) as
          PlatformFields | undefined) ?? platformFields;
      const override = freshPlatformFields[platformKey] as
        Record<string, unknown> | undefined;
      const isCurrentlyUnlinked = Boolean(
        override && override.unlinked === true
      );
      const hasEdits = Object.keys(override || {}).some(
        (k) => k !== "unlinked"
      );
      const doToggle = () => dispatch(togglePlatformLink(currentFormId));

      if (
        isCurrentlyUnlinked &&
        hasEdits &&
        entitiesFormContext?.confirmDiscardOverride
      ) {
        entitiesFormContext.confirmDiscardOverride(doToggle);
      } else {
        doToggle();
      }
    },
    [
      flushPending,
      platformFields,
      dispatch,
      currentFormId,
      entitiesFormContext,
      store,
    ]
  );

  const fieldsRef = useRef(null);
  const sortedAccounts = sortAccounts(Object.values(formAccounts || []));

  const allDisabled =
    sortedAccounts.length === 0 || isLoading || Boolean(post.past);

  const disabledWhenNotUnlinked =
    selectedPlatform !== "LINKED" &&
    (!platformFields[selectedPlatform] ||
      platformFields[selectedPlatform].unlinked !== true);

  const platformFormValue = useMemo(
    () => ({
      ...postFormValue,
      ...(selectedPlatform !== "LINKED" && platformFields[selectedPlatform]),
      platformType: selectedPlatform,
    }),
    [platformFields, postFormValue, selectedPlatform]
  );

  const {
    showVideoSelector,
    isLoading: isLoadingVideo,
    videoMedia,
    videoUrl,
    thumbnails,
    thumbnailUrl,
    videoLoadingError,
    originalMediaUrl,
    lowQualityMediaUrl,
    coverFrames,
    lazyBlobRef,
    videoDurationFromMeta,
  } = useVideoCover({
    selectedPlatform,
    platforms: platforms,
    platformPost,
    thumbCount: bulkTab ? 6 : 10,
  });

  useEffect(() => {
    if (!showVideoSelector) {
      onCoverPreviewChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVideoSelector]);

  if (!platformPost) {
    return "";
  }
  let captionHashtagCount = countCharacter(
    platformFormValue?.caption ?? "",
    "#"
  );
  let firstCommentHashtagCount = countCharacter(
    platformFormValue?.firstcomment ?? "",
    "#"
  );
  const hasInsta = sortedAccounts.find(
    (account) => account.platformName === "instagram"
  );

  if (hasInsta) {
    captionHashtagCount = firstCommentHashtagCount =
      captionHashtagCount + firstCommentHashtagCount;
  }

  const hasYT = platforms.some((platform) => platform.TYPE === "YT");

  const captionLengthLimitPost =
    selectedPlatform === "LINKED"
      ? { unlinked: platformFormValue.unlinked }
      : {
          unlinked: platformFormValue.unlinked,
          platformType: platformFormValue.platformType as Exclude<
            FormPlatformType,
            "LINKED"
          >,
        };

  const captionMaxLength = getCaptionLengthLimit(
    captionLengthLimitPost,
    platforms.filter(
      (platform) => platformFields[platform.TYPE]?.unlinked !== true
    )
  );

  function handleMediaUpdate(mediaFiles: unknown) {
    updateField(FIELDS.MEDIA, mediaFiles);
  }

  function handleImageAltUpdate(value: unknown) {
    updateField(FIELDS.ALT_TEXT, value);
  }

  function handleOpenAddAlt() {
    setOpenAddAlt(!openAddAlt);
  }

  const onCaptionFieldChange = (val: string) => {
    updateField(FIELDS.CAPTION, val);
  };

  const isTwitter = selectedPlatform === "TW";

  const { hasMoreThanOnelinkedInAccount, selectedLinkedInAccountType } =
    getLinkedInAccountDetail(postFormValue.accounts);
  const captionWarnings = useMemo(() => {
    const shouldShowTwitterWarning =
      selectedPlatform === "LINKED" &&
      platforms.some((platform) => platform.TYPE === "TW");
    const twitterWarning = shouldShowTwitterWarning
      ? getTwitterPaidAccountWarning(
          platformFormValue,
          postFormValue.accounts || []
        )
      : null;

    if (!twitterWarning) {
      return warnings;
    }

    const existingWarnings =
      (warnings?.[FIELDS.CAPTION]?.warnings as string[] | undefined) || [];

    if (existingWarnings.includes(twitterWarning)) {
      return warnings;
    }

    return {
      ...warnings,
      [FIELDS.CAPTION]: {
        warnings: [...existingWarnings, twitterWarning],
      },
    };
  }, [
    platformFormValue,
    platforms,
    postFormValue.accounts,
    selectedPlatform,
    warnings,
  ]);

  // @codeme Break this up into smaller components
  return (
    <PostFormProvider
      id={platformFormValue._id}
      disabled={Boolean(allDisabled)}
    >
      {isTwitter ? (
        <TwitterClusterForm
          post={platformPost}
          unlinked={platformFields?.TW?.unlinked}
          toggleUnlinked={() => {
            trackEvent({ eventName: UNLINK_TOGGLE_TW });
            handleToggleUnlink("TW");
          }}
          errors={errors}
          warnings={warnings}
          platforms={platforms}
          formId={bulkTab?.formId}
          captionEditHistory={captionEditHistory}
          captionHashtagCount={captionHashtagCount}
          selectedContentLabel={selectedContentLabel}
          accountGroupId={accountGroupId}
          labelIds={postFormValue?.labelIds || []}
          accounts={postFormValue.accounts || []}
        />
      ) : (
        <>
          {/* Platform post disconnection toggle */}
          {selectedPlatform !== "LINKED" && (
            <div className="flex flex-col mb-4">
              <TooltipWrapper
                tooltip={`Custom changes for ${PLATFORMS[selectedPlatform].LABEL} will apply to all ${PLATFORMS[selectedPlatform].LABEL} accounts that you have selected.`}
                className="flex items-center justify-start gap-2"
              >
                <div className="font-inter text-sm font-medium leading-5 text-neutral-800">
                  Customise {PLATFORMS[selectedPlatform].LABEL} post
                </div>
                <Toggle
                  key={`unlink-toggle-${selectedPlatform}`}
                  defaultChecked={Boolean(
                    platformFields[selectedPlatform] &&
                    platformFields[selectedPlatform].unlinked === true
                  )}
                  icons={{
                    unchecked: null,
                  }}
                  className="active-purple compact-toggle"
                  onChange={() => {
                    trackEvent({
                      eventName: `unlink-toggle-${selectedPlatform}`,
                    });
                    handleToggleUnlink(selectedPlatform);
                  }}
                />
              </TooltipWrapper>
            </div>
          )}

          {/* Wrapper for platform fields */}
          <div
            ref={fieldsRef}
            className="relative"
            key={`fields-${selectedPlatform}-${
              !platformFields[selectedPlatform] ||
              platformFields[selectedPlatform].unlinked !== true
                ? "default"
                : "custom"
            }`}
          >
            {/* Overlay for a linked platform */}
            {disabledWhenNotUnlinked && (
              <PlatformFieldsOverlay fieldsRef={fieldsRef} />
            )}

            <PostAlerts
              postFormValue={platformFormValue}
              oldTiktokAccounts={oldTiktokAccounts}
              platforms={platforms}
              selectedPlatform={selectedPlatform}
              ttIsNotification={ttIsNotification}
              isThreadsPostingEnabled={true}
              oldThreadsAccounts={oldThreadsAccounts}
              thIsNotification={thIsNotification}
            />

            <div>
              {/* Story Toggle - disabled version for when viewing with linked */}
              {/* This is so that customers can 'see' that we support these posts */}
              {selectedPlatform === "LINKED" &&
                platforms.length > 1 &&
                platforms.some((platform) => platform.TYPE === "IG") && (
                  <div className="flex flex-col mb-4">
                    <TooltipWrapper
                      placement={"top"}
                      tooltip={
                        "To post an Instagram story or send a notification, remove non-Instagram or notification channels."
                      }
                    >
                      <div className="flex items-center justify-start gap-2">
                        <div className="font-inter text-sm font-medium leading-5 text-neutral-800">
                          Switch to Instagram Story
                        </div>
                        <Toggle
                          key={`story-toggle-${selectedPlatform}`}
                          defaultChecked={
                            postFormValue[FIELDS.IS_STORY] || false
                          }
                          icons={{
                            unchecked: null,
                          }}
                          className="active-purple compact-toggle"
                          disabled={true}
                        />
                      </div>
                    </TooltipWrapper>
                  </div>
                )}
              {selectedPlatform === "TT" && (
                <TiktokSwitchNotification
                  platforms={platforms}
                  value={ttIsNotification}
                  hasTikTokPosting={hasTikTokPosting}
                  updateField={updateField}
                  allDisabled={allDisabled || disabledWhenNotUnlinked}
                  oldTiktokAccounts={oldTiktokAccounts}
                />
              )}
              {selectedPlatform === "TH" && (
                <ThreadsSwitchNotification
                  platforms={platforms}
                  value={thIsNotification}
                  hasThreadsPosting={hasThreadsPosting}
                  updateField={updateField}
                  allDisabled={allDisabled || disabledWhenNotUnlinked}
                  oldThreadsAccounts={oldThreadsAccounts}
                />
              )}
              <VideoReelToggle
                mediaFiles={(postFormValue?.mediaFiles || []) as never}
                selectedPlatform={selectedPlatform}
                formAccounts={formAccounts}
                updateField={updateField}
                disabled={allDisabled || disabledWhenNotUnlinked}
                currentValue={platformPost?.videoPostType}
                platformPost={platformPost as never}
              />
              {showVideoSelector && (
                <VideoCoverSelector
                  disabled={
                    selectedPlatform !== "LINKED" &&
                    platformFields?.[selectedPlatform]?.unlinked !== true
                  }
                  originalMediaUrl={originalMediaUrl}
                  lowQualityMediaUrl={lowQualityMediaUrl}
                  videoUrl={videoUrl}
                  isLoading={isLoadingVideo}
                  videoMedia={videoMedia}
                  thumbnails={thumbnails}
                  updateFields={updateFields}
                  platforms={platforms}
                  selectedPlatform={selectedPlatform}
                  thumbnailUrl={platformPost?.thumbnailUrl}
                  videoCoverPreview={platformPost?.videoCoverPreview}
                  videoCoverType={platformPost?.videoCoverType}
                  videoCoverOriginalTimestamp={
                    platformPost?.videoCoverOriginalTimestamp
                  }
                  videoCoverOriginalType={platformPost?.videoCoverOriginalType}
                  videoCoverOriginal={platformPost?.videoCoverOriginal}
                  originalCover={
                    platformPost?.videoCoverOriginal ||
                    platformPost?.thumbnailUrl ||
                    thumbnailUrl
                  }
                  videoCoverImageTimestamp={
                    platformPost?.videoCoverImageTimestamp
                  }
                  videoLoadingError={videoLoadingError}
                  onCoverPreviewChange={onCoverPreviewChange}
                  coverFrames={coverFrames}
                  lazyBlobRef={lazyBlobRef}
                  videoDurationFromMeta={videoDurationFromMeta}
                />
              )}
              {/* Caption */}
              <CaptionField
                key={`caption-${selectedPlatform}`}
                className="mb-5 flex flex-col"
                label={
                  <>
                    <div className="mr-1">Caption</div>

                    {hasYT && ["LINKED", "YT"].includes(selectedPlatform) && (
                      <div className="mr-1">(YouTube video description)</div>
                    )}
                    <PlatformUsage
                      platforms={platforms}
                      field={FIELDS.CAPTION}
                      selectedPlatform={selectedPlatform}
                    />
                  </>
                }
                selectedPlatform={selectedPlatform}
                value={platformFormValue.caption ?? ""}
                characterCount={getCaptionCharacterCount(
                  platformFormValue as Post,
                  platforms,
                  selectedPlatform,
                  postFormValue.accounts || []
                )}
                onChange={onCaptionFieldChange}
                maxLength={captionMaxLength}
                hashtagCount={captionHashtagCount}
                errors={errors}
                warnings={captionWarnings}
                disabled={allDisabled || disabledWhenNotUnlinked}
                platforms={platforms}
                hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
                selectedLinkedInAccountType={selectedLinkedInAccountType}
                disableMention={selectedPlatform === "LINKED"}
                formId={bulkTab?.formId}
                captionEditHistory={captionEditHistory}
                selectedContentLabel={selectedContentLabel}
                accountGroupId={accountGroupId}
                labelIds={postFormValue?.labelIds || []}
              />

              {/* First Comment */}
              {visibleFields.includes(FIELDS.FIRST_COMMENT) && (
                <PostFirstComment
                  key={`first-comment-${selectedPlatform}`}
                  post={platformPost}
                  selectedPlatform={selectedPlatform}
                  platforms={platforms}
                  className="mb-5 flex flex-col"
                  onChange={(val) => updateField(FIELDS.FIRST_COMMENT, val)}
                  hashtagCount={firstCommentHashtagCount}
                  errors={errors}
                  warnings={warnings}
                  disabled={allDisabled || disabledWhenNotUnlinked}
                  hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
                  selectedLinkedInAccountType={selectedLinkedInAccountType}
                  formId={bulkTab?.formId}
                  selectedContentLabel={selectedContentLabel}
                  accountGroupId={accountGroupId}
                  labelIds={postFormValue?.labelIds || []}
                />
              )}

              {/* URL */}
              {showUrlField && (
                <PostUrl
                  url={platformFormValue.redirectUrl ?? undefined}
                  checkSetting={true}
                  errors={errors}
                  selectedPlatform={selectedPlatform as never}
                  selectedAccounts={formAccounts}
                  platforms={platforms}
                  className="mb-5"
                  title="URL"
                  onChange={(newUrl) => updateField(FIELDS.URL, newUrl)}
                  disabled={allDisabled || disabledWhenNotUnlinked}
                />
              )}

              {/* TT other fields */}
              {hasTikTokPosting && (
                <PlatformOptionsCard
                  title="TikTok Options"
                  iconClassName="fa-brands fa-tiktok"
                >
                  <TiktokSwitchAllows
                    postFormValue={platformFormValue}
                    updateField={updateField}
                    allDisabled={allDisabled || disabledWhenNotUnlinked}
                    platforms={platforms}
                    selectedPlatform={selectedPlatform}
                    errors={errors}
                  />
                </PlatformOptionsCard>
              )}
              {/* Threads fields */}
              {hasThreadsPosting && (
                <PlatformOptionsCard
                  title="Threads Options"
                  iconClassName="fa-brands fa-threads"
                >
                  <ThreadsReplyControl
                    key={`thReplyControlId-${selectedPlatform}`}
                    title={`Reply Control`}
                    post={platformPost}
                    selectedPlatform={selectedPlatform as never}
                    platforms={platforms}
                    selectedAccounts={formAccounts}
                    onChange={(val) =>
                      updateField(FIELDS.TH_REPLY_CONTROL, val)
                    }
                    errors={errors}
                    disabled={
                      allDisabled ||
                      (selectedPlatform !== "LINKED" &&
                        (!platforms || (platforms && platforms.length > 1)) &&
                        (!platformFields[selectedPlatform] ||
                          platformFields[selectedPlatform].unlinked !== true))
                    }
                  />
                </PlatformOptionsCard>
              )}
              {/* Tags */}
              {hasPostTags && (
                <PostTags
                  hasTikTokPosting={hasTikTokPosting}
                  post={platformPost as never}
                  selectedPlatform={selectedPlatform}
                  platforms={platforms}
                  visibleFields={visibleFields}
                  className="flex mb-2"
                  onChange={updateField}
                  handleOpenAddAlt={handleOpenAddAlt}
                  isSummary={isSummary}
                />
              )}
            </div>
            {/* GMB Fields */}
            {visibleFields.includes(FIELDS.GMB_TOPIC_TYPE) && (
              <PlatformOptionsCard
                title="Google Business Profile Options"
                iconClassName="fa-brands fa-google"
              >
                <GmbFields
                  onChange={updateFields}
                  post={mergeGmbPostFields(
                    platformFormValue as unknown as Record<string, unknown>,
                    platformFields[selectedPlatform] as unknown as
                      Record<string, unknown> | undefined
                  )}
                  selectedPlatform={selectedPlatform}
                  platforms={platforms}
                  className="flex-col"
                  disabled={allDisabled || disabledWhenNotUnlinked}
                />
              </PlatformOptionsCard>
            )}
            {/* YT Fields */}
            {hasYT && visibleFields.includes(FIELDS.YT_TITLE) && (
              <PlatformOptionsCard
                title="YouTube Options"
                iconClassName="fa-brands fa-youtube text-red-500"
              >
                <YtFields
                  onChange={updateField}
                  selectedAccounts={formAccounts}
                  post={{
                    ...platformFormValue,
                    ...platformFields[selectedPlatform],
                  }}
                  selectedPlatform={selectedPlatform}
                  platforms={platforms}
                  disabled={allDisabled || disabledWhenNotUnlinked}
                />
              </PlatformOptionsCard>
            )}
            {/* Pinterest fields */}
            {visibleFields.includes(FIELDS.PI_BOARDS) && (
              <PlatformOptionsCard
                title="Pinterest Options"
                iconClassName="fa-brands fa-pinterest text-red-600"
              >
                <PinterestFields
                  onChange={updateField}
                  selectedAccounts={formAccounts}
                  post={{
                    ...platformFormValue,
                    ...platformFields[selectedPlatform],
                  }}
                  selectedPlatform={selectedPlatform}
                  platforms={platforms}
                  disabled={allDisabled || disabledWhenNotUnlinked}
                />
              </PlatformOptionsCard>
            )}
          </div>

          {openAddAlt && (
            <AltTextModal
              post={
                platformFormValue as unknown as import("features/post/format-post").NgPost
              }
              toggleAddAlt={handleOpenAddAlt}
              handleImageAltUpdate={handleImageAltUpdate}
              onSubmit={
                handleMediaUpdate as (
                  value: import("features/post/format-post").NgMediaFile[]
                ) => void
              }
              platforms={platforms}
            />
          )}
        </>
      )}
    </PostFormProvider>
  );
}

function getCaptionLengthLimit(
  post:
    | {
        unlinked?: boolean;
        platformType?: string;
      }
    | undefined,
  platforms: PlatformSpec[]
) {
  const defaultCharLimit = CHARACTER_LIMITS["FB"];
  // if post is unlinked 👉 get individual char limit
  if (post?.unlinked && post.platformType) {
    const platformType = post.platformType as keyof typeof CHARACTER_LIMITS;
    return CHARACTER_LIMITS[platformType] || defaultCharLimit;
  }
  // if not 👉 get minimum required
  else {
    const limits = platforms.map(
      (p: PlatformSpec) => CHARACTER_LIMITS[p.TYPE] || defaultCharLimit
    );

    return limits.length === 0 ? defaultCharLimit : Math.min(...limits);
  }
}

function getCaptionCharacterCount(
  post: Partial<Post> | undefined,
  platforms: PlatformSpec[],
  selectedPlatform: FormPlatformType,
  accounts: AccountType[] = []
) {
  return getPostCaptionCharacterCount(
    (post ?? {}) as Post,
    platforms,
    selectedPlatform,
    accounts
  );
}

function TwitterClusterForm(props: {
  post: Post;
  unlinked: boolean | undefined;
  toggleUnlinked: () => void;
  errors?: FormValidationErrors;
  warnings?: FormValidationWarnings;
  platforms: PlatformSpec[];
  formId?: string;
  captionEditHistory?: SkedEditorHistory;
  captionHashtagCount?: number;
  selectedContentLabel?: Label[];
  accountGroupId?: string;
  labelIds?: string[];
  accounts?: AccountType[];
}) {
  const {
    post: _postFormValue,
    platforms,
    formId,
    captionEditHistory,
    captionHashtagCount,
  } = props;
  const captionField = useFormField("caption");
  const redirectUrlField = useFormField("redirectUrl");
  const captionWarnings = useMemo(() => {
    const twitterWarning = getTwitterPaidAccountWarning(
      props.post,
      props.accounts || []
    );

    if (!twitterWarning) {
      return props.warnings;
    }

    const existingWarnings =
      (props.warnings?.[FIELDS.CAPTION]?.warnings as string[] | undefined) ||
      [];

    if (existingWarnings.includes(twitterWarning)) {
      return props.warnings;
    }

    return {
      ...props.warnings,
      [FIELDS.CAPTION]: {
        warnings: [...existingWarnings, twitterWarning],
      },
    };
  }, [props.accounts, props.post, props.warnings]);

  return (
    <div>
      <UnlinkToggle
        label="Customise Twitter post"
        value={props.unlinked}
        onChange={props.toggleUnlinked}
      />

      <CaptionField
        className="flex flex-col mb-2"
        label="Caption"
        value={props.post.caption ?? ""}
        characterCount={getTwitterEffectiveCaptionLength(props.post)}
        onChange={captionField.input.onChange}
        maxLength={CHARACTER_LIMITS.TW}
        errors={props.errors}
        warnings={captionWarnings}
        disabled={!props.unlinked}
        platforms={platforms}
        formId={formId}
        captionEditHistory={captionEditHistory}
        selectedContentLabel={props.selectedContentLabel}
        selectedPlatform="TW"
        hashtagCount={captionHashtagCount}
        accountGroupId={props.accountGroupId}
        labelIds={props.labelIds}
      />

      <PostUrl
        url={props.post.redirectUrl ?? undefined}
        checkSetting={true}
        errors={props.errors}
        className="mb-2"
        title="URL"
        onChange={redirectUrlField.input.onChange}
        disabled={!props.unlinked}
      />
    </div>
  );
}

// only used for Twitter currently (?) - fix tooltip label to refer to Twitter
function UnlinkToggle(props: {
  label: string;
  value?: boolean;
  onChange: () => void;
}) {
  return (
    <div className="flex flex-col mb-4">
      <TooltipWrapper
        tooltip={`Custom changes for Twitter will apply to all Twitter accounts that you have selected.`}
        className="flex items-center justify-start gap-2"
      >
        <div className="font-inter text-sm font-medium leading-5 text-neutral-800">
          {props.label}
        </div>
        <Toggle
          defaultChecked={props.value}
          icons={{
            unchecked: null,
          }}
          className="active-purple compact-toggle"
          onChange={props.onChange}
        />
      </TooltipWrapper>
    </div>
  );
}

// TODO move it to libs
const PostFormContext = React.createContext<{
  change: (fieldName: string, value: unknown) => void;
  formDisabled: boolean;
} | null>(null);

function PostFormProvider(props: {
  id: string;
  disabled: boolean;
  children: React.ReactNode;
}) {
  const dispatch = useDispatch();

  const change = useCallback(
    (fieldName: string, value: unknown) => {
      dispatch(updatePostFormField(props.id, { [fieldName]: value }));
    },
    [dispatch, props.id]
  );

  const formContext = useMemo(
    () => ({ change, formDisabled: props.disabled }),
    [change, props.disabled]
  );

  return (
    <PostFormContext.Provider value={formContext}>
      {props.children}
    </PostFormContext.Provider>
  );
}

function useFormField(fieldName: string) {
  const formContext = useContext(PostFormContext);

  return {
    input: {
      value: "",
      onChange(value: unknown) {
        formContext?.change(fieldName, value);
      },
    },
  };
}
